import React, { useState, useEffect } from 'react';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';
import { Autocomplete, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import './RequestedEcommerceOrder.css';
import { local_signage, local_url } from '../../Const';
import Header from '../../Header';

export const SaleOrderChangeReport = () => {
  // State variables
  const [areas, setAreas] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState('ALL');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchInputValue, setBranchInputValue] = useState('');
  const [selectedOption, setSelectedOption] = useState('LIST');
  const [requestedStatus, setRequestedStatus] = useState('CR');
  const [approvalStatus, setApprovalStatus] = useState('OK');
  const [fromDate, setFromDate] = useState(dayjs().subtract(30, 'day'));
  const [toDate, setToDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // New filter for table data
  const [fetchingData, setFetchingData] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(100);
  const [tableWidth, setTableWidth] = useState(0);
  const [tableHeight, setTableHeight] = useState(0);

  // User authentication
  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("AuthorizationToken");

  // Option types
  const optionTypes = [
    { value: 'LIST', label: 'List' }
  ];

  // Status options
  const statusOptions = [
    { value: 'AL', label: 'ALL' },
    { value: 'CR', label: 'Cancellation Request' },
    { value: 'DR', label: 'Deviation Request' },
    { value: 'HR', label: 'Hold Request' },
    { value: 'OK', label: 'Completed' },
    { value: 'RJ', label: 'Rejected' }
  ];

  // Status filter options for table filtering
  const statusFilterOptions = [
    { value: 'AL', label: 'ALL Status' },
    { value: 'CR', label: 'Cancellation Request' },
    { value: 'DR', label: 'Deviation Request' },
    { value: 'HR', label: 'Hold Request' },
    { value: 'OK', label: 'Completed' },
    { value: 'RJ', label: 'Rejected' }
  ];

  // Status label mapping
  const getStatusLabel = (status) => {
    const statusMap = {
      'CR': 'Cancellation Request',
      'DR': 'Deviation Request',
      'HR': 'Hold Request',
      'OK': 'Completed',
      'RJ': 'Rejected'
    };
    return statusMap[status] || status;
  };

  // DatePicker styles
  const datePickerStyle = {
    width: '100%',
    maxWidth: '140px',
  };

  // Calculate table dimensions based on viewport
  useEffect(() => {
    const updateTableDimensions = () => {
      const windowHeight = window.innerHeight;
      const estimatedOtherComponentsHeight = 300;
      const calculatedHeight = windowHeight - estimatedOtherComponentsHeight;

      setTableHeight(Math.max(calculatedHeight, 400));
      setTableWidth(window.innerWidth - 40);
    };

    updateTableDimensions();
    window.addEventListener('resize', updateTableDimensions);

    return () => {
      window.removeEventListener('resize', updateTableDimensions);
    };
  }, []);

  // Check for authentication on component mount
  useEffect(() => {
    if (!authToken) {
      setError("Authorization token is missing. Please log in again.");
      redirectToLogin();
      return;
    }
  }, []);

  // Fetch areas on component mount
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const response = await fetch(`${local_signage}/api/v1/sfareabysegment?SegmentCode=LAZIZ`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch areas by segment');
        }

        const data = await response.json();
        console.log('Fetched areas for segment:', data);
        setAreas(data);
      } catch (error) {
        console.error('Error fetching areas by segment:', error);
        setAreas([]);
      }
    };

    if (authToken) {
      fetchAreas();
    }
  }, [authToken]);

  // Fetch branches when area changes
  useEffect(() => {
    fetchBranchList();
  }, [selectedAreaId]);

  // Redirect to login
  const redirectToLogin = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  // Handle authentication error
  const handleAuthError = () => {
    setError("Session expired. Please log in again.");
    redirectToLogin();
  };

  // Fetch branch list
  const fetchBranchList = async () => {
    if (!authToken) {
      console.error('Authentication token is missing');
      handleAuthError();
      return;
    }

    try {
      // Use the selected area ID for API call with LAZIZ as default segment
      const areaParam = selectedAreaId || "ALL";
      const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/LAZIZ`;

      console.log('Fetching branches from URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error('Failed to fetch branch list');
      }

      const data = await response.json();
      console.log('Fetched branch list:', data);
      setBranchList(data);
    } catch (error) {
      console.error('Error fetching branch list:', error);
      setBranchList([]);
    }
  };

  // Date validation
  const validateDates = () => {
    if (!fromDate || !toDate) {
      setError("Please select both From Date and To Date");
      return false;
    }
    
    if (fromDate.isAfter(toDate)) {
      setError("From Date cannot be after To Date");
      return false;
    }
    
    // Check if date range is not more than 365 days
    const daysDiff = toDate.diff(fromDate, 'day');
    if (daysDiff > 365) {
      setError("Date range cannot exceed 365 days");
      return false;
    }
    
    return true;
  };

  // Filter data based on search query and status filter
  useEffect(() => {
    if (data.length > 0) {
      let filtered = [...data];

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(item =>
          (item.SaleOrderId && item.SaleOrderId.toLowerCase().includes(query)) ||
          (item.FromBranchID && item.FromBranchID.toLowerCase().includes(query)) ||
          (item.RequestedStatus && item.RequestedStatus.toLowerCase().includes(query)) ||
          (item.BranchName && item.BranchName.toLowerCase().includes(query))
        );
      }

      // Filter by requested status
      if (statusFilter && statusFilter !== 'ALL') {
        filtered = filtered.filter(item => 
          item.RequestedStatus && item.RequestedStatus === statusFilter
        );
      }

      // Apply sorting if configured
      if (sortConfig.key && sortConfig.direction) {
        filtered.sort((a, b) => {
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        });
      }

      setFilteredData(filtered);
      setCurrentPage(1);
    } else {
      setFilteredData([]);
    }
  }, [data, searchQuery, statusFilter, sortConfig]);

  // Handle form field changes
  const handleAreaChange = (e) => {
    setSelectedAreaId(e.target.value);
    // Reset branch selection when area changes
    setSelectedBranch(null);
    setBranchInputValue('');
  };

  const handleBranchChange = (event, value) => {
    setSelectedBranch(value);
  };

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const handleRequestedStatusChange = (e) => {
    setRequestedStatus(e.target.value);
  };

  const handleApprovalStatusChange = (e) => {
    setApprovalStatus(e.target.value);
  };

  // Handle search input change
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  // Format date for API request
  const formatDateForApi = (date) => {
    return date.format('YYYYMMDD');
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateDates()) {
      return;
    }

    if (!authToken) {
      setError("Authorization token is missing. Please log in again.");
      redirectToLogin();
      return;
    }

    setFetchingData(true);
    setError('');

    try {
      const formattedFromDate = formatDateForApi(fromDate);
      const formattedToDate = formatDateForApi(toDate);
      const branchId = selectedBranch?.branchID || 'ALL';

      const url = `${local_signage}/api/v1/saleorderchangereport?Option=${selectedOption}&AreaId=${selectedAreaId}&FromBranchId=${branchId}&RequestedStatusID=${requestedStatus}&ApprovalStatusId=${approvalStatus}&FromDate=${formattedFromDate}&ToDate=${formattedToDate}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError("Session expired. Please log in again.");
          redirectToLogin();
          return;
        }
        throw new Error("Failed to fetch data");
      }

      const responseData = await response.json();
      setData(responseData);
      setFetchingData(false);
    } catch (error) {
      setError("Failed to fetch data. Please try again later.");
      setFetchingData(false);
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Format date and time
  const formatDateTime = (dateTimeString, type) => {
    if (!dateTimeString) return "-";

    const date = new Date(dateTimeString);

    if (isNaN(date.getTime())) return "-";

    if (type === "date") {
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } else if (type === "time") {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }

    return "-";
  };

  // Export data to CSV
  const exportToCSV = () => {
    if (filteredData.length === 0) return;

    setExportLoading(true);

    try {
      // Create CSV header - removed SF Area ID, SF Area Name, and Request Reason Code
      const headers = [
        "Sr.No", "Sale Order ID", "From Branch ID", "Branch Name", "Requested Status", 
        "Created User ID", "Created Date", "Created Time", "Current Approval Status", 
        "Closed", "Attended By User ID", "Attended Date", "Attended Time", "Request Reason Description"
      ];

      // Format data for CSV
      const csvData = filteredData.map((item, index) => [
        index + 1,
        item.SaleOrderId || "-",
        item.FromBranchID || "-",
        item.BranchName || "-",
        getStatusLabel(item.RequestedStatus) || "-",
        item.CreatedUserId || "-",
        formatDateTime(item.CreatedDate, "date"),
        formatDateTime(item.CreatedDate, "time"),
        item.CurrentApprovalStatus || "-",
        item.closed ? "Yes" : "No",
        item.AttendedbyUserId || "-",
        formatDateTime(item.AttendedDateTime, "date"),
        formatDateTime(item.AttendedDateTime, "time"),
        item.RequestReasonDesc || "-"
      ]);

      // Combine header and data
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.join(','))
      ].join('\n');

      // Create a blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `SaleOrderChangeReport_${new Date().toISOString().slice(0, 100)}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setExportLoading(false);
    } catch (error) {
      setError("Failed to export data. Please try again later.");
      setExportLoading(false);
    }
  };

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const displayData = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    let pageNumbers = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

      if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (startPage > 1) {
        pageNumbers = [1, '...', ...pageNumbers.slice(2)];
      }

      if (endPage < totalPages) {
        pageNumbers = [...pageNumbers.slice(0, -2), '...', totalPages];
      }
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Sort Header Component
  const SortHeader = ({ column, label }) => (
    <th onClick={() => handleSort(column)} className="requested-sortable-header">
      {label}
      {sortConfig.key === column && (
        <span className="requested-sort-indicator">{sortConfig.direction === 'ascending' ? '▲' : '▼'}</span>
      )}
    </th>
  );

  // Full Screen Loader Component
  const FullScreenLoader = () => (
    <div className="requested-fullscreen-loader">
      <div className="requested-loader-content">
        <div className="requested-loader-spinner">
          <div className="requested-spinner-ring"></div>
          <div className="requested-spinner-ring"></div>
          <div className="requested-spinner-ring"></div>
          <div className="requested-spinner-ring"></div>
        </div>
        <p className="requested-loader-text">Loading data, please wait...</p>
      </div>
    </div>
  );

  return (
    <>
      <Header />

      {/* Full Screen Loader */}
      {fetchingData && <FullScreenLoader />}

      <div className="requested-ecommerce-order-container">
        {error && <div className="requested-error-message">{error}</div>}

        <div className="requested-filters-container">
          <div className="requested-filter-row">
            <div className="requested-filter-group">
              {/* Options Dropdown */}
              <select
                className="requested-filter-dropdown"
                value={selectedOption}
                onChange={handleOptionChange}
              >
                {optionTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              {/* Area Dropdown */}
              <FormControl variant="outlined" style={{ minWidth: 180, maxWidth: 180 }}>
                <InputLabel id="area-select-label">Area</InputLabel>
                <Select
                  labelId="area-select-label"
                  value={selectedAreaId}
                  onChange={handleAreaChange}
                  label="Area"
                  size="small"
                >
                  <MenuItem value="ALL">All</MenuItem>
                  {areas.map((area) => (
                    <MenuItem key={area.SFAreaId} value={area.SFAreaId}>
                      {area.SFAreaName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Branch Autocomplete */}
              <Autocomplete
                style={{ minWidth: 220, maxWidth: 220 }}
                options={branchList || []}
                getOptionLabel={(option) => option.branchName || ""}
                value={selectedBranch}
                onChange={handleBranchChange}
                inputValue={branchInputValue}
                onInputChange={(event, newInputValue) => {
                  setBranchInputValue(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Branch"
                    variant="outlined"
                    size="small"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.branchID === value?.branchID}
              />

              {/* Requested Status Dropdown */}
              <FormControl variant="outlined" style={{ minWidth: 170, maxWidth: 170 }}>
                <InputLabel id="requested-status-select-label">Request Status</InputLabel>
                <Select
                  labelId="requested-status-select-label"
                  value={requestedStatus}
                  onChange={handleRequestedStatusChange}
                  label="Request Status"
                  size="small"
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Approval Status Dropdown */}
              <FormControl variant="outlined" style={{ minWidth: 170, maxWidth: 170 }}>
                <InputLabel id="approval-status-select-label">Approval Status</InputLabel>
                <Select
                  labelId="approval-status-select-label"
                  value={approvalStatus}
                  onChange={handleApprovalStatusChange}
                  label="Approval Status"
                  size="small"
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="requested-date-filters">
              <div className="requested-date-picker-wrapper">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    format="DD-MM-YYYY"
                    sx={datePickerStyle}
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "outlined"
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div className="requested-date-separator">
                <span>-</span>
              </div>

              <div className="requested-date-picker-wrapper">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    format="DD-MM-YYYY"
                    sx={datePickerStyle}
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "outlined"
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>

            <div className="requested-action-buttons">
              <button
                onClick={handleSubmit}
                className="requested-submit-button"
                disabled={loading || fetchingData}
              >
                {fetchingData ? "Loading..." : "Submit"}
              </button>
              <button
                onClick={exportToCSV}
                className="requested-export-button"
                disabled={loading || filteredData.length === 0 || exportLoading}
              >
                {exportLoading ? "Exporting..." : "Export"}
              </button>
            </div>

            <div className="requested-search-row">
              <div className="requested-search-container">
                <input
                  type="text"
                  placeholder="Search Sale Order ID, Branch ID/Name"
                  value={searchQuery}
                  onChange={handleSearch}
                  className="requested-search-input"
                />
              </div>
              
              {/* Status Filter Dropdown */}
              <div className="requested-status-filter-container">
                <select
                  className="requested-filter-dropdown"
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  style={{ marginLeft: '10px' }}
                >
                  {statusFilterOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Pagination Controls - Top */}
        {filteredData.length > recordsPerPage && (
          <div className="requested-pagination-container">
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className="requested-pagination-button"
            >
              First
            </button>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="requested-pagination-button"
            >
              Prev
            </button>

            {pageNumbers.map((number, index) => (
              number === "..." ?
                <span key={`ellipsis-${index}`} className="requested-pagination-ellipsis">...</span> :
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={`requested-pagination-button ${currentPage === number ? 'requested-pagination-active' : ''}`}
                >
                  {number}
                </button>
            ))}

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="requested-pagination-button"
            >
              Next
            </button>
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className="requested-pagination-button"
            >
              Last
            </button>
            <span className="requested-pagination-info">
              Page {currentPage} of {totalPages}
            </span>
          </div>
        )}

        {/* Table Component with Fixed Headers and Scrollable Body */}
        <div className="requested-table-container" style={{ height: `${tableHeight}px` }}>
          <div className="requested-table-wrapper">
            <table className="requested-data-table">
              <thead>
                <tr>
                  <th>Sr.No</th>
                  <SortHeader column="SaleOrderId" label="Sale Order ID" />
                  <SortHeader column="FromBranchID" label="From Branch ID" />
                  <SortHeader column="BranchName" label="Branch Name" />
                  <SortHeader column="RequestedStatus" label="Requested Status" />
                  <SortHeader column="CreatedUserId" label="Created User ID" />
                  <SortHeader column="CreatedDate" label="Created Date" />
                  <SortHeader column="CreatedDate" label="Created Time" />
                  <SortHeader column="CurrentApprovalStatus" label="Current Approval Status" />
                  <SortHeader column="closed" label="Closed" />
                  <SortHeader column="AttendedbyUserId" label="Attended By User ID" />
                  <SortHeader column="AttendedDateTime" label="Attended Date" />
                  <SortHeader column="AttendedDateTime" label="Attended Time" />
                  <SortHeader column="RequestReasonDesc" label="Request Reason Description" />
                </tr>
              </thead>
              <tbody>
                {displayData.length > 0 ? (
                  displayData.map((item, index) => {
                    const actualIndex = (currentPage - 1) * recordsPerPage + index;
                    return (
                      <tr key={index}>
                        <td>{actualIndex + 1}</td>
                        <td>{item.SaleOrderId || "-"}</td>
                        <td>{item.FromBranchID || "-"}</td>
                        <td>{item.BranchName || "-"}</td>
                        <td>{getStatusLabel(item.RequestedStatus) || "-"}</td>
                        <td>{item.CreatedUserId || "-"}</td>
                        <td>{formatDateTime(item.CreatedDate, "date")}</td>
                        <td>{formatDateTime(item.CreatedDate, "time")}</td>
                        <td>{item.CurrentApprovalStatus || "-"}</td>
                        <td>{item.closed ? "Yes" : "No"}</td>
                        <td>{item.AttendedbyUserId || "-"}</td>
                        <td>{formatDateTime(item.AttendedDateTime, "date")}</td>
                        <td>{formatDateTime(item.AttendedDateTime, "time")}</td>
                        <td>{item.RequestReasonDesc || "-"}</td>
                      </tr>
                    );
                  })
                ) : !fetchingData ? (
                  <tr>
                    <td colSpan="14" className="requested-no-data-message">
                      No data available
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default SaleOrderChangeReport;